import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '../../hooks/useSnackbar'
import {
  useChangeGoodAllocationTruckStatusMutation,
  useGoodAllocTruckStatusLookupQuery,
} from '../../store/api/procurementGoodAllocationApi'
import type { GoodAllocationListItem, GoodAllocationLookupItem } from '../../types/api'
import { getErrorMessage } from '../../utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
} from '../purchase/dialogStyle'

export interface GoodAllocationTruckStatusDialogProps {
  open: boolean
  rows: GoodAllocationListItem[]
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

/**
 * Change Truck Status dialog — converts Access form
 * `frmProcurementQryGoodAlc_ChangeStatus` (Procurement - Change Truck Status).
 *
 * Opened for a set of ticked Goods Allocation rows. Exposes a single combobox
 * `truck_status` (Access RowSource `public.tmp_truck_status`) and, on Save,
 * applies `{ ids, truck_status }` to all selected records.
 *
 * Combobox is a flat Autocomplete (options from the truck-status lookup). No
 * fullScreen; mobile stacks to a single column with internal scroll.
 */
export default function GoodAllocationTruckStatusDialog({
  open,
  rows,
  canEdit,
  onClose,
  onSaved,
}: GoodAllocationTruckStatusDialogProps) {
  const snack = useSnackbar()
  const [changeStatus, { isLoading: isSaving }] =
    useChangeGoodAllocationTruckStatusMutation()
  const { data: lookupData, isFetching: lookupLoading } =
    useGoodAllocTruckStatusLookupQuery(undefined, { skip: !open })

  const [truckStatus, setTruckStatus] = useState<string>('')

  // Re-init each time the dialog opens.
  useEffect(() => {
    if (!open) return
    setTruckStatus('')
  }, [open])

  const options = useMemo<GoodAllocationLookupItem[]>(
    () => lookupData?.items ?? [],
    [lookupData],
  )

  const value = useMemo(
    () => options.find((it) => it.value === truckStatus) ?? null,
    [options, truckStatus],
  )

  const handleSave = async () => {
    if (!canEdit || !truckStatus) return
    try {
      await changeStatus({
        ids: rows.map((r) => r.id),
        truck_status: truckStatus,
      }).unwrap()
      snack.success('Successfully Changed!')
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to change truck status'))
    }
  }

  const recordCount = rows.length

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={dialogTitleSx}>
        <Typography
          component="span"
          noWrap
          title="Inbound Logistics - Change Truck Status"
          sx={dialogTitleTextSx}
        >
          Inbound Logistics - Change Truck Status
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            variant="contained"
            size="small"
            onClick={() => void handleSave()}
            disabled={isSaving || !canEdit || !truckStatus}
            sx={dialogHeaderBtnSx}
          >
            {isSaving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={isSaving}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {`${recordCount} ${recordCount === 1 ? 'record' : 'records'} selected`}
        </Typography>

        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr' }}>
          <Autocomplete<GoodAllocationLookupItem>
            size="small"
            options={options}
            loading={lookupLoading}
            value={value}
            getOptionLabel={(o) => o.label ?? ''}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_e, val) => setTruckStatus(val?.value ?? '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Truck Status *"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {lookupLoading ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
